import type { ListBuilderConfiguration } from '../types';

/**
 * Sourcing Request Files Embedded list configuration
 * Shows files attached to a sourcing request
 * Converted from JSON to TypeScript for type safety
 */
export const SOURCING_REQUEST_FILES_EMBEDDED_CONFIG: ListBuilderConfiguration =
	{
		lists: [
			{
				id: 'default-sourcing-request-files-embedded',
				cacheTTL: 300000,
				name: 'Files',
				type: 'default',
				active: true,
				sortOrder: 0,
				columns: [
					{
						id: 'name-column',
						fields: [
							{
								id: 'file-name',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'fileName',
										objectTable: 'sourcing_request_file',
									},
								},
								link: {
									type: 'callback',
									enabled: true,
									callbackName: 'view',
								},
								styleConfig: {
									size: 'sm',
									weight: 'medium',
									colorRule: { type: 'theme', themeColor: 'primary' },
								},
								displayStyle: 'text',
							},
						],
						headers: [{ id: 'h-name', text: 'Name' }],
						sortable: true,
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'fileName',
								objectTable: 'sourcing_request_file',
							},
						],
					},
					{
						id: 'type-column',
						fields: [
							{
								id: 'file-type',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'fileType',
										objectTable: 'sourcing_request_file',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'theme', themeColor: 'textSecondary' },
								},
								displayStyle: 'text',
							},
						],
						headers: [{ id: 'h-type', text: 'Type' }],
						width: 150,
						sortable: true,
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'fileType',
								objectTable: 'sourcing_request_file',
							},
						],
					},
					{
						// "Line" column — shows which SR line the file is
						// pinned to (file → line many-to-one via
						// `_fk_sourcing_request_line__id`). Renders as
						// "Line N: <description>" when present, dash
						// otherwise (file is attached at SR-level not
						// line-level). The lineNumber and lineDescription
						// columns are surfaced by the backend
						// getFilesPaginated raw-select.
						id: 'line-column',
						fields: [
							{
								id: 'line-display',
								dataConfig: {
									type: 'template',
									template: 'Line {lineNumber}: {lineDescription}',
									templateFields: [
										{ fieldName: 'lineNumber' },
										{ fieldName: 'lineDescription' },
									],
									defaultValue: '-',
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'theme', themeColor: 'textSecondary' },
								},
								displayStyle: 'text',
							},
						],
						headers: [{ id: 'h-line', text: 'Line' }],
						width: 200,
						sortable: false,
					},
					{
						id: 'date-column',
						fields: [
							{
								id: 'upload-date',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: '_created_date',
										objectTable: 'sourcing_request_file',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'default' },
									formatOptions: { dateFormat: 'date-medium' },
								},
								displayStyle: 'date',
							},
						],
						headers: [{ id: 'h-date', text: 'Uploaded' }],
						width: 130,
						sortable: true,
						sortFields: [
							{
								direction: 'DESC',
								fieldName: '_created_date',
								objectTable: 'sourcing_request_file',
							},
						],
					},
					{
						id: 'uploader-column',
						fields: [
							{
								id: 'uploader-name',
								dataConfig: {
									type: 'template',
									template: '{uploadedByFirstName} {uploadedByLastName}',
									templateFields: [],
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'theme', themeColor: 'textSecondary' },
								},
								displayStyle: 'text',
							},
						],
						headers: [{ id: 'h-uploader', text: 'Uploaded By' }],
						width: 140,
						sortable: true,
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'uploadedByFirstName',
								objectTable: 'sourcing_request_file',
							},
						],
					},
					{
						id: 'actions-column',
						fields: [
							{
								id: 'actions-dropdown',
								dataConfig: { type: 'custom_text', customText: '' },
								styleConfig: {
									size: 'md',
									colorRule: { type: 'default' },
								},
								displayStyle: 'actions_dropdown',
								dropdownActions: [
									{
										id: 'view-action',
										label: 'View',
										iconName: 'eye',
										callbackName: 'view',
										variant: 'secondary',
										visibility: 'visible',
									},
									{
										id: 'download-action',
										label: 'Download',
										iconName: 'download',
										callbackName: 'download',
										variant: 'secondary',
										visibility: 'overflow',
									},
									{
										id: 'delete-action',
										label: 'Delete',
										iconName: 'trash',
										callbackName: 'delete',
										danger: true,
										visibility: 'overflow',
										requiredPermission: 'sourcing_requests:update',
									},
								],
							},
						],
						headers: [{ id: 'h-actions', text: '' }],
						width: 80,
						align: 'center',
						sortable: false,
					},
				],
				defaultSortId: 'column-date-column-desc',
			},
		],
		paging: {
			defaultPageSize: 25,
			pageSizeOptions: [10, 25, 50, 100],
		},
		filters: {
			activeFilters: [],
			defaultFilters: {},
		},
		objects: [
			{
				objectLabel: 'Sourcing Request File',
				objectTable: 'sourcing_request_file',
				selectedFields: [
					{ fieldName: '_id', fieldLabel: 'ID' },
					{
						fieldName: 'fk_sourcing_request__id',
						fieldLabel: 'Sourcing Request ID',
					},
					{ fieldName: 'fk_file__id', fieldLabel: 'File ID' },
					{ fieldName: 'fileName', fieldLabel: 'File Name' },
					{ fieldName: 'fileType', fieldLabel: 'File Type' },
					{ fieldName: 'fileSize', fieldLabel: 'File Size' },
					{ fieldName: '_created_date', fieldLabel: 'Upload Date' },
					{
						fieldName: 'uploadedByFirstName',
						fieldLabel: 'Uploader First Name',
					},
					{ fieldName: 'uploadedByLastName', fieldLabel: 'Uploader Last Name' },
					{
						fieldName: '_fk_sourcing_request_line__id',
						fieldLabel: 'Line ID',
					},
					{ fieldName: 'lineNumber', fieldLabel: 'Line Number' },
					{ fieldName: 'lineDescription', fieldLabel: 'Line Description' },
				],
			},
		],
		tableSorts: [
			{
				id: 'date-desc',
				name: 'Upload Date (Newest First)',
				fields: [
					{
						fieldName: '_created_date',
						objectTable: 'sourcing_request_file',
						direction: 'DESC',
					},
				],
			},
			{
				id: 'date-asc',
				name: 'Upload Date (Oldest First)',
				fields: [
					{
						fieldName: '_created_date',
						objectTable: 'sourcing_request_file',
						direction: 'ASC',
					},
				],
			},
			{
				id: 'name-asc',
				name: 'File Name (A-Z)',
				fields: [
					{
						fieldName: 'fileName',
						objectTable: 'sourcing_request_file',
						direction: 'ASC',
					},
				],
			},
			{
				id: 'name-desc',
				name: 'File Name (Z-A)',
				fields: [
					{
						fieldName: 'fileName',
						objectTable: 'sourcing_request_file',
						direction: 'DESC',
					},
				],
			},
		],
	};
